<script setup lang="ts">
import type { IconNode } from 'lucide';
import type { IconNode as IconNodeNext } from 'lucide-vue-next';
import { createLucideIcon } from 'lucide-vue-next';
import { computed, toRaw } from 'vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<IconProps>(), {
    size: 'md',
    ariaLabel: undefined,
    svgAttributes: undefined,
});

export type IconSize = 'inherit' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export interface IconProps {
    /**
     * Icon data to render as SVG icon.
     */
    icon: IconNode
    /**
     * Size of the icon. `inherit` binds it to the surrounding font size instead of a fixed step.
     * Can also be defined via CSS variable `--sph-icon-size`.
     * Default: 'md'
     */
    size?: IconSize
    /**
     * Human-readable label for assistive technologies when the icon is meaningful.
     * When provided, the icon will be exposed with role="img".
     */
    ariaLabel?: string

    svgAttributes?: Record<string, string>
}

const iconComponent = computed(() => {
    const data = toRaw(props.icon);
    if (!data) {
        return undefined;
    }

    return createLucideIcon('icon', data as IconNodeNext)({}, {} as any);
});
</script>

<template>
    <span
        class="sph-icon"
        :class="[props.size && `size-${props.size}`]"
        :aria-label="props.ariaLabel || undefined"
        :role="props.ariaLabel ? 'img' : undefined"
        :aria-hidden="!props.ariaLabel ? 'true' : undefined"
    >
        <component
            :is="iconComponent"
            v-bind="props.svgAttributes"
        />
    </span>
</template>

<style>
@reference '@/assets/styles/index.css';

.sph-icon {
    --sph-icon-size: var(--sph-icon-size, calc(var(--spacing) * 6));

    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--sph-icon-size);
    height: var(--sph-icon-size);
    flex: none;
    line-height: 0;
    user-select: none;

    svg {
        --stroke-width: var(--sph-icon-stroke-width, 1.375);

        display: block;
        width: 100%;
        height: 100%;
        stroke: var(--sph-icon-color, currentColor);
        fill: none;

        g,
        path,
        circle,
        rect,
        line,
        polyline,
        polygon,
        ellipse {
            stroke-width: var(--stroke-width);
        }
    }

    /* Binds the icon to the type it sits in, for an icon set inline with text: it scales with
       font-size rather than holding a fixed step, and lucide's artwork spans 18 of its 24 viewBox
       units, so 1em draws at 0.75em — a shade over cap height, which is where an inline icon
       belongs. The stroke matches a regular-weight stem at the same size. */
    &.size-inherit {
        --sph-icon-size: 1em;
        --sph-icon-stroke-width: 2;
    }

    /* equals: size-{x} */
    &.size-xs {
        --sph-icon-size: calc(var(--spacing) * 4);
        --sph-icon-stroke-width: 1.75;
    }
    &.size-sm {
        --sph-icon-size: calc(var(--spacing) * 5);
        --sph-icon-stroke-width: 1.5;
    }
    &.size-md {
        --sph-icon-size: calc(var(--spacing) * 6);
        --sph-icon-stroke-width: 1.375;
    }
    &.size-lg {
        --sph-icon-size: calc(var(--spacing) * 7);
        --sph-icon-stroke-width: 1.25;
    }
    &.size-xl {
        --sph-icon-size: calc(var(--spacing) * 8);
        --sph-icon-stroke-width: 1.125;
    }
    &.size-xxl {
        --sph-icon-size: calc(var(--spacing) * 9);
        --sph-icon-stroke-width: 1.125;
    }
}
</style>
